/**
 * Serviço do construtor de questionários (form-builder).
 *
 * Objetivo:
 * - Persistir questionários via API FHIR usando o recurso `Questionnaire`.
 * - Usar `fhirClients.admin` para operações CRUD.
 * - Expor uma fachada de domínio para que aplicações não dependam da
 *   implementação de FHIR.
 */

import { fhirClients } from "src-next/shared/api/fhirClient";
import type { QuestionnairePayload } from "../types";
import {
  toFhirQuestionnaireFromPayload,
  toPayloadFromFhirQuestionnaire,
} from "../utils/questionnaireQuestionnaireTransformers";
import {
  QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM,
} from "../utils/questionnaireQuestionnaireConstants";

function getQuestionnaireClient() {
  return fhirClients.admin.resource<fhir5.Questionnaire>("Questionnaire");
}

function buildIdentifierToken(params: { system: string; value: string }) {
  // FHIR token search for `identifier` uses `system|value`.
  return `${params.system}|${params.value}`;
}

interface GetByIdentifierParams {
  system: string;
  value: string;
}

interface GetByOrganizationIdentifierParams {
  organizationId: string;
}

/**
 * Persiste o questionário como rascunho (status draft).
 * Se payload.id existir, faz update; caso contrário, create.
 */
async function saveDraft(
  data: QuestionnairePayload,
  organizationId: string,
): Promise<QuestionnairePayload> {
  const client = getQuestionnaireClient();
  const fhirResource = toFhirQuestionnaireFromPayload({
    payload: data,
    status: "draft",
    existingId: data.id,
    organizationId,
  });

  if (data.id) {
    await client.update(data.id, fhirResource);
    return { ...data, id: data.id };
  }

  const created = await client.create(fhirResource);
  const payload = toPayloadFromFhirQuestionnaire(created);
  if (!payload) {
    throw new Error("Resposta inválida ao criar questionário");
  }
  return { ...payload, id: created.id };
}

/**
 * Persiste edição intermediária (status unknown no FHIR).
 *
 * Uso: fluxo “Editar” a partir de Gerenciar. Quem lista apenas `active` não
 * enxerga esta versão até `save` (publicação).
 */
async function saveEditingUnknown(
  data: QuestionnairePayload,
  organizationId: string,
): Promise<QuestionnairePayload> {
  const client = getQuestionnaireClient();
  const fhirResource = toFhirQuestionnaireFromPayload({
    payload: data,
    status: "unknown",
    existingId: data.id,
    organizationId,
  });

  if (data.id) {
    await client.update(data.id, fhirResource);
    return { ...data, id: data.id };
  }

  const created = await client.create(fhirResource);
  const payload = toPayloadFromFhirQuestionnaire(created);
  if (!payload) {
    throw new Error("Resposta inválida ao criar questionário");
  }
  return { ...payload, id: created.id };
}

/**
 * Persiste o questionário como publicado (status active).
 * Se payload.id existir, faz update; caso contrário, create.
 */
async function save(
  data: QuestionnairePayload,
  organizationId: string,
): Promise<QuestionnairePayload> {
  const client = getQuestionnaireClient();
  const fhirResource = toFhirQuestionnaireFromPayload({
    payload: data,
    status: "active",
    existingId: data.id,
    organizationId,
  });

  if (data.id) {
    await client.update(data.id, fhirResource);
    return { ...data, id: data.id };
  }

  const created = await client.create(fhirResource);
  const payload = toPayloadFromFhirQuestionnaire(created);
  if (!payload) {
    throw new Error("Resposta inválida ao criar questionário");
  }
  return { ...payload, id: created.id };
}

/**
 * Lista questionários (drafts e ativos).
 */
async function list(): Promise<QuestionnairePayload[]> {
  const client = getQuestionnaireClient();
  const bundle = await client.search({ _count: "100" });

  const entries = bundle.entry ?? [];

  const payloads = entries
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Questionnaire =>
        resource !== undefined && resource.resourceType === "Questionnaire",
    )
    .map((resource) => toPayloadFromFhirQuestionnaire(resource))
    .filter((p): p is QuestionnairePayload => p !== null);

  return payloads;
}

/**
 * Recupera um questionário pelo id.
 */
async function getById(id: string): Promise<QuestionnairePayload | null> {
  const client = getQuestionnaireClient();
  try {
    const resource = await client.read(id);
    return toPayloadFromFhirQuestionnaire(resource);
  } catch {
    return null;
  }
}

/**
 * Lê o recurso FHIR bruto (ex.: montar mapa linkId → texto com itens aninhados).
 */
async function readFhirById(id: string): Promise<fhir5.Questionnaire | null> {
  const client = getQuestionnaireClient();
  try {
    return await client.read(id);
  } catch {
    return null;
  }
}

/**
 * Busca um questionário por `identifier` (FHIR token search).
 *
 * Ex.: identifier=system|value
 */
async function getByIdentifier(
  params: GetByIdentifierParams,
): Promise<QuestionnairePayload | null> {
  const client = getQuestionnaireClient();
  const identifierToken = buildIdentifierToken(params);

  const bundle = await client.search({
    identifier: identifierToken,
    _count: 1,
  });

  const resource = (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .find(
      (candidate): candidate is fhir5.Questionnaire =>
        candidate !== undefined &&
        candidate.resourceType === "Questionnaire",
    );

  if (!resource) return null;
  return toPayloadFromFhirQuestionnaire(resource);
}

/**
 * Busca um questionário usando o mesmo `identifier` criado pelo transformer.
 *
 * `identifier[0].value` => `Organization/{organizationId}`
 */
async function getByOrganizationIdentifier(
  params: GetByOrganizationIdentifierParams,
): Promise<QuestionnairePayload | null> {
  const organizationReference = `Organization/${params.organizationId}`;

  return getByIdentifier({
    system: QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM,
    value: organizationReference,
  });
}

export const questionnaireBuilderService = {
  save,
  saveDraft,
  saveEditingUnknown,
  list,
  getById,
  readFhirById,
  getByIdentifier,
  getByOrganizationIdentifier,
};
